import { Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

export type MpsTheme = 'light' | 'dark' | 'auto';
export type MpsColorTheme = 'blue' | 'green' | 'purple' | 'red';

@Injectable({
  providedIn: 'root',
})
export class MpsThemeService {
  private currentTheme$ = new BehaviorSubject<MpsTheme>('light');
  private currentColorTheme$ = new BehaviorSubject<MpsColorTheme>('blue');

  constructor() {
    // Initialiser avec le thème sauvegardé ou système
    this.initializeTheme();

    // Exposer le service dans window pour debug
    if (typeof window !== 'undefined') {
      (window as any).mpsThemeService = this;
    }
  }

  /**
   * Observable du thème actuel
   */
  get theme$() {
    return this.currentTheme$.asObservable();
  }

  /**
   * Observable du thème de couleur actuel
   */
  get colorTheme$() {
    return this.currentColorTheme$.asObservable();
  }

  /**
   * Obtenir le thème actuel
   */
  getCurrentTheme(): MpsTheme {
    return this.currentTheme$.value;
  }

  /**
   * Obtenir le thème de couleur actuel
   */
  getCurrentColorTheme(): MpsColorTheme {
    return this.currentColorTheme$.value;
  }

  /**
   * Changer le thème principal (light/dark/auto)
   */
  setTheme(theme: MpsTheme): void {
    this.currentTheme$.next(theme);
    this.applyTheme(theme);
    this.saveThemePreference(theme);
  }

  /**
   * Changer le thème de couleur
   */
  setColorTheme(colorTheme: MpsColorTheme): void {
    this.currentColorTheme$.next(colorTheme);
    this.applyColorTheme(colorTheme);
    this.saveColorThemePreference(colorTheme);
  }

  /**
   * Basculer entre light et dark
   */
  toggleTheme(): void {
    const current = this.getCurrentTheme();
    const newTheme: MpsTheme = current === 'light' ? 'dark' : 'light';
    this.setTheme(newTheme);
  }

  /**
   * Initialiser le thème au démarrage
   */
  private initializeTheme(): void {
    const savedTheme = this.getSavedTheme();
    const savedColorTheme = this.getSavedColorTheme();

    if (savedTheme) {
      this.setTheme(savedTheme);
    } else {
      // Détecter la préférence système
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      this.setTheme(prefersDark ? 'dark' : 'light');
    }

    if (savedColorTheme) {
      this.setColorTheme(savedColorTheme);
    }

    // Écouter les changements de préférence système
    window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', (e) => {
      if (this.getCurrentTheme() === 'auto') {
        this.applyTheme('auto');
      }
    });
  }

  /**
   * Appliquer le thème au DOM
   */
  private applyTheme(theme: MpsTheme): void {
    //console.log('🎨 Applying theme:', theme);
    const root = document.documentElement;
    try {
      // Supprimer les anciens attributs
      root.removeAttribute('data-theme');
      //console.log('🔄 Removed previous data-theme');

      if (theme === 'auto') {
        // Laisser le navigateur décider via @media (prefers-color-scheme)
        //console.log('🔄 Auto theme - using system preferences');

        theme = window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
      }

      // Apply Material mode
      root.classList.remove('light', 'dark'); // retire tous les thèmes
      root.classList.add(theme); // <-- important pour Angular Material

      // Appliquer le thème spécifique
      root.setAttribute('data-theme', theme);
      //console.log(`✅ Applied data-theme="${theme}"`);

      // Vérifier que l'attribut a bien été appliqué
      const appliedTheme = root.getAttribute('data-theme');
      console.log('🔍 Verification - applied theme:', appliedTheme);
    } catch (error) {
      console.error('❌ Error applying theme:', error);
    }
  }

  /**
   * Appliquer le thème de couleur au DOM
   */
  private applyColorTheme(colorTheme: MpsColorTheme): void {
    console.log('🌈 Applying color theme:', colorTheme);
    const root = document.documentElement;

    try {
      const allColorThemes = ['mps-theme-blue', 'mps-theme-green', 'mps-theme-purple', 'mps-theme-red'];

      // Supprimer les anciennes classes de thème
      root.classList.remove(...allColorThemes);
      console.log('🔄 Removed previous color theme classes');

      // Ajouter la nouvelle classe
      const newClass = `mps-theme-${colorTheme}`;
      root.classList.add(newClass);
      console.log(`✅ Applied color theme class: ${newClass}`);

      // Vérifier que la classe a bien été appliquée
      const hasClass = root.classList.contains(newClass);
      console.log('🔍 Verification - has class:', hasClass);
      console.log('🔍 Current classes:', root.classList.toString());
    } catch (error) {
      console.error('❌ Error applying color theme:', error);
    }
  }

  /**
   * Sauvegarder la préférence de thème
   */
  private saveThemePreference(theme: MpsTheme): void {
    localStorage.setItem('mps-theme', theme);
  }

  /**
   * Sauvegarder la préférence de couleur
   */
  private saveColorThemePreference(colorTheme: MpsColorTheme): void {
    localStorage.setItem('mps-color-theme', colorTheme);
  }

  /**
   * Récupérer le thème sauvegardé
   */
  private getSavedTheme(): MpsTheme | null {
    const saved = localStorage.getItem('mps-theme');
    return saved as MpsTheme | null;
  }

  /**
   * Récupérer le thème de couleur sauvegardé
   */
  private getSavedColorTheme(): MpsColorTheme | null {
    const saved = localStorage.getItem('mps-color-theme');
    return saved as MpsColorTheme | null;
  }

  /**
   * Méthode de debug pour vérifier l'état actuel du DOM
   */
  debugCurrentState(): void {
    const root = document.documentElement;
    console.log('🔍 DEBUG - Current DOM state:');
    console.log('  - data-theme:', root.getAttribute('data-theme'));
    console.log('  - classList:', root.classList.toString());
    console.log('  - Current theme service state:', this.getCurrentTheme());
    console.log('  - Current color theme service state:', this.getCurrentColorTheme());
    console.log('  - Saved theme in localStorage:', this.getSavedTheme());
    console.log('  - Saved color theme in localStorage:', this.getSavedColorTheme());
  }

  /**
   * Forcer la re-application des thèmes
   */
  forceReapplyThemes(): void {
    console.log('🔄 Force reapplying themes...');
    this.applyTheme(this.getCurrentTheme());
    this.applyColorTheme(this.getCurrentColorTheme());
  }

  /**
   * Tester si les CSS variables existent
   */
  testCssVariables(): void {
    console.log('🧪 Testing CSS variables...');
    const root = document.documentElement;
    const computedStyle = getComputedStyle(root);

    const testVariables = ['--mps-view-background', '--mps-primary-color', '--mps-text-primary'];

    testVariables.forEach((variable) => {
      const value = computedStyle.getPropertyValue(variable);
      this.logColorVariable(variable, value);
    });

    // Tester avec data-theme
    root.setAttribute('data-theme', 'dark');
    const darkStyle = getComputedStyle(root);
    console.log('🌙 After setting dark theme:');
    testVariables.forEach((variable) => {
      const value = darkStyle.getPropertyValue(variable);
      this.logColorVariable(variable, value);
    });
  }

  /**
   * Affiche une variable CSS avec sa couleur visualisée
   */
  private logColorVariable(variable: string, value: string): void {
    const cleanValue = value.trim();

    if (this.isValidColor(cleanValue)) {
      // Affichage avec couleur de fond dans la console
      console.log(
        `%c  ${variable}: "${cleanValue}"`,
        `background-color: ${cleanValue}; color: ${this.getContrastColor(cleanValue)}; padding: 2px 8px; border-radius: 3px; font-family: monospace;`
      );
    } else {
      // Affichage normal pour les valeurs non-couleur
      console.log(`  ${variable}: "${cleanValue}"`);
    }
  }

  /**
   * Vérifie si une valeur est une couleur valide
   */
  private isValidColor(value: string): boolean {
    // Regex pour détecter les couleurs hex, rgb, rgba, hsl, hsla
    const colorRegex = /^(#[0-9a-f]{3,8}|rgb\(|rgba\(|hsl\(|hsla\()/i;
    return colorRegex.test(value) || this.isNamedColor(value);
  }

  /**
   * Vérifie si c'est une couleur nommée CSS
   */
  private isNamedColor(value: string): boolean {
    const namedColors = [
      'white',
      'black',
      'red',
      'blue',
      'green',
      'yellow',
      'orange',
      'purple',
      'pink',
      'gray',
      'grey',
    ];
    return namedColors.includes(value.toLowerCase());
  }

  /**
   * Calcule une couleur de contraste pour le texte
   */
  private getContrastColor(backgroundColor: string): string {
    // Conversion simplifiée pour déterminer si on utilise blanc ou noir
    if (backgroundColor.includes('#')) {
      const hex = backgroundColor.replace('#', '');
      const r = parseInt(hex.substr(0, 2), 16);
      const g = parseInt(hex.substr(2, 2), 16);
      const b = parseInt(hex.substr(4, 2), 16);
      const brightness = (r * 299 + g * 587 + b * 114) / 1000;
      return brightness > 128 ? '#000000' : '#ffffff';
    }

    // Pour les autres formats, on utilise blanc par défaut
    return '#ffffff';
  }

  /**
   * Test complet du système de thème
   */
  runFullTest(): void {
    console.log('🚀 Running full theme system test...');

    // 1. État initial
    this.debugCurrentState();

    // 2. Test des variables CSS
    this.testCssVariables();

    // 3. Test de changement de thème
    console.log('🔄 Testing theme changes...');
    this.setTheme('dark');
    setTimeout(() => {
      this.debugCurrentState();
      this.setTheme('light');
      setTimeout(() => {
        this.debugCurrentState();
      }, 100);
    }, 100);

    // 4. Test de changement de couleur
    console.log('🌈 Testing color theme changes...');
    this.setColorTheme('green');
    setTimeout(() => {
      this.debugCurrentState();
    }, 200);
  }
}
